/*
    Engagement workflow definition: stages, items, and URL slugs per engagement type.
    Item `key` values are the API contract with the backend setup-status endpoint
    (CmdGetEngagementSetupStatus) — never rename one side only.
*/

export type WorkflowItemDef = {
  key: string; // backend item key, e.g. "network_resources"
  slug: string; // URL segment, e.g. "network-resources"
  name: string;
  /**
   * A page being trialled alongside the one it may replace. The backend setup-status
   * endpoint knows nothing about it, so it has no completion state and must not move the
   * progress count — adding it to a counting stage would otherwise turn "8 of 11" into
   * "8 of 12" on every engagement and never complete.
   */
  preview?: boolean;
  /**
   * A permission feature the user must hold to see this item at all. Omitted for the
   * items everyone with the Engagement Tool gets, which is all of them but one.
   */
  feature?: string;
};

export type WorkflowStageDef = {
  key: string;
  name: string;
  countsTowardProgress: boolean;
  items: WorkflowItemDef[];
};

const item = (key: string, name: string): WorkflowItemDef => ({
  key,
  slug: key.replace(/_/g, "-"),
  name,
});

/** A page on trial beside its incumbent. Same stage, no status, no weight on progress. */
const previewItem = (key: string, name: string): WorkflowItemDef => ({
  ...item(key, name),
  preview: true,
});

/*
    Stage names are what an ad ops team would call these, not process nouns:
    what the client has to give us, what we build from it, and what happens once
    it is out the door. Note "Launch" is deliberately not used as a stage name —
    a Launch is a campaign in this codebase, and the two readings collide.
*/
const REACH_WORKFLOW: WorkflowStageDef[] = [
  {
    key: "setup",
    name: "Setup",
    countsTowardProgress: true,
    items: [
      item("contract", "Contract"),
      item("branding", "Branding"),
      item("data_file", "Population"),
      item("team", "Team"),
      item("network_resources", "Network Resources"),
      item("sftp", "SFTP"),
    ],
  },
  {
    key: "build",
    name: "Build",
    countsTowardProgress: true,
    items: [
      // Plural where the step lists many: the singular read as though an
      // engagement only ever had one campaign or one audience.
      item("campaign", "Campaigns"),
      item("audience", "Audiences"),
      item("ad_groups", "Ad Groups"),
      // MPN-2204. Sits with the other things built per campaign and before Schedule, which
      // shows the sends it produces. Preview because the setup-status endpoint has no key
      // for it yet, so it has no completion state to count.
      //
      // Restricted to Ops and super-admin while the feature is under test (MPN-2197), the
      // same arrangement as Media Spend above. Widening the grant in the server's permission
      // seed is what turns it on for more roles.
      { ...previewItem("email_sms", "Email & SMS"), feature: "Email & SMS" },
      item("schedule", "Schedule"),
      item("budget", "Budget"),
      // MPN-1792. Reads the same budget tree and header as Budget above — one source,
      // two presentations — so the two can be compared on the same engagement. It is named
      // for what it holds rather than for the screen it replaces: once Budget goes, "Budget
      // 2.0" would be a version number with nothing to be a version of.
      // Restricted to Ops and super-admin while it is under test (MPN-2041). The gate is
      // its own feature rather than the Engagement Tool, which also reaches Analytics and
      // Power User — and misses Super Admin entirely. The server enforces the same
      // feature on this screen's writes; this only decides whether it is reachable.
      { ...previewItem("budget_2", "Media Spend"), feature: "Media Spend" },
    ],
  },
  { key: "launch", name: "Launch", countsTowardProgress: false, items: [item("publish", "Publish")] },
  { key: "monitor", name: "Monitor", countsTowardProgress: false, items: [item("monitor", "Monitor")] },
  { key: "close", name: "Close", countsTowardProgress: false, items: [item("close", "Close")] },
];

/*
    A CREATE engagement produces content: there is no data file, no audience,
    and nothing is published to an ad network. Team is exposed because Request
    Content fills it in implicitly and it should be inspectable. Close stays —
    it is how the engagement is retired — but it only marks the engagement
    closed, with no networks to archive.
*/
const CREATE_WORKFLOW: WorkflowStageDef[] = [
  {
    key: "setup",
    name: "Setup",
    countsTowardProgress: true,
    items: [item("team", "Team")],
  },
  { key: "content", name: "Content", countsTowardProgress: true, items: [item("content", "Content")] },
  { key: "close", name: "Close", countsTowardProgress: false, items: [item("close", "Close")] },
];

export const OVERVIEW_SLUG = "overview";
export const NOTES_SLUG = "notes";

export function getWorkflow(engagementType: string): WorkflowStageDef[] {
  return engagementType === "CREATE" ? CREATE_WORKFLOW : REACH_WORKFLOW;
}

export function getWorkflowItemBySlug(
  engagementType: string,
  slug: string
): WorkflowItemDef | undefined {
  return getWorkflow(engagementType)
    .flatMap((stage) => stage.items)
    .find((i) => i.slug === slug);
}

export type WorkflowDisplayGroup = {
  key: string;
  name: string;
  numbered: boolean;
  items: WorkflowItemDef[];
};

/*
    Presentation grouping: the stages you work through keep their numbered
    headings; the single-item stages that follow (Publish/Monitor/Close) collapse
    into one unnumbered "Live" group, since they are not steps you complete.
*/
export function getWorkflowDisplayGroups(engagementType: string): WorkflowDisplayGroup[] {
  const stages = getWorkflow(engagementType);
  const groups: WorkflowDisplayGroup[] = stages
    .filter((stage) => stage.countsTowardProgress)
    .map((stage) => ({ key: stage.key, name: stage.name, numbered: true, items: stage.items }));
  const lifecycleItems = stages
    .filter((stage) => !stage.countsTowardProgress)
    .flatMap((stage) => stage.items);
  if (lifecycleItems.length) {
    groups.push({ key: "live", name: "Live", numbered: false, items: lifecycleItems });
  }
  return groups;
}

export type ContentWidth = "narrow" | "wide" | "full";

/*
    How wide each item's content pane is. Default is "full" — editors and
    heavy components (contract editor, setup, schedule, monitor) want the
    whole pane. Exceptions are declared here: "wide" for descending lists,
    "narrow" for centered empty/choice screens.
*/
const CONTENT_WIDTH_BY_SLUG: Record<string, ContentWidth> = {
  [OVERVIEW_SLUG]: "wide",
  [NOTES_SLUG]: "wide",
};

export function getContentWidth(slug: string): ContentWidth {
  return CONTENT_WIDTH_BY_SLUG[slug] ?? "full";
}


/**
 * Whether a workflow item is visible to this user.
 *
 * Visibility only — the server gates the writes independently, so a user who reaches the
 * page some other way still cannot change anything.
 */
export function canSeeWorkflowItem(
  item: WorkflowItemDef,
  hasAccess: (feature: string) => boolean,
): boolean {
  return !item.feature || hasAccess(item.feature);
}
